  import React, { createContext, useContext, ReactNode, useState } from "react";
import {
  Address,
  Carwash,
  Owner,
  RegisterData,
  SignUp,
  HeroSignUp,
  SurveyData,
} from "../types/RegisterType";
import { savePreRegistration } from "../utils/preRegistration";

interface RegisterContextProps {
  registerAddress: (address: Address) => void;
  registerOwner: (owner: Owner) => void;
  registerCarwash: (carwash: Carwash) => void;
  registerSignup: (signUp: SignUp) => void;
  registerHeroSignUp: (heroSignUp: HeroSignUp) => void;
  setSurveyData: (survey: SurveyData) => void;
  getRegisterData: () => RegisterData;
  canProceed: () => boolean;
}

const RegisterContext = createContext<RegisterContextProps | undefined>(
  undefined
);

export const RegisterProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const [address, setAddress] = useState<Address | null>(null);
  const [owner, setOwner] = useState<Owner | null>(null);
  const [carwash, setCarwash] = useState<Carwash | null>(null);
  const [signup, setSignup] = useState<SignUp | null>(null);
  const [survey, setSurvey] = useState<SurveyData | null>(null);

  const registerAddress = (address: Address): void => {
    setAddress({ ...address, country: "Brasil" });
    // Salvar pré-cadastro na etapa 1 (Endereço)
    setTimeout(() => {
      savePreRegistration(1, {
        address: { ...address, country: "Brasil" },
        owner,
        carwash,
        signup,
        survey,
      });
    }, 100);
  };

  const registerOwner = (owner: Owner): void => {
    setOwner(owner);
    // Salvar pré-cadastro na etapa 2 (Responsável)
    setTimeout(() => {
      savePreRegistration(2, {
        address,
        owner,
        carwash,
        signup,
        survey,
      });
    }, 100);
  };

  const registerCarwash = (carwash: Carwash): void => {
    setCarwash(carwash);
    // Salvar pré-cadastro na etapa 3 (Negócio)
    setTimeout(() => {
      savePreRegistration(3, {
        address,
        owner,
        carwash,
        signup,
        survey,
      });
    }, 100);
  };

  const registerSignup = (signup: SignUp): void => {
    setSignup(signup);
    // Salvar pré-cadastro na etapa 6 (Login)
    setTimeout(() => {
      savePreRegistration(6, {
        address,
        owner,
        carwash,
        signup,
        survey,
      });
    }, 100);
  };

  const registerHeroSignUp = (heroSignUp: HeroSignUp): void => {
    setOwner({ full_name: heroSignUp.full_name, last_name: heroSignUp.last_name });
    setCarwash({ phone: heroSignUp.phone });
    setSignup({
      email: heroSignUp.email,
      receive_communications: heroSignUp.receive_communications,
      privacy_terms: heroSignUp.privacy_terms,
    });
    // Salvar pré-cadastro inicial (Hero SignUp)
    setTimeout(() => {
      savePreRegistration(0, {
        address,
        owner: { full_name: heroSignUp.full_name, last_name: heroSignUp.last_name },
        carwash: { phone: heroSignUp.phone },
        signup: {
          email: heroSignUp.email,
          receive_communications: heroSignUp.receive_communications,
          privacy_terms: heroSignUp.privacy_terms,
        },
        survey,
      });
    }, 100);
  };

  const setSurveyData = (survey: SurveyData): void => {
    setSurvey(survey);
    // Salvar pré-cadastro na etapa 4 (Pesquisa)
    setTimeout(() => {
      savePreRegistration(4, {
        address,
        owner,
        carwash,
        signup,
        survey,
      });
    }, 100);
  };

  const getRegisterData = (): RegisterData => {
    return {
      address,
      owner,
      carwash,
      signup,
      survey,
    };
  };

  const canProceed = (): boolean => {
    if (!owner?.full_name && !carwash?.phone && !signup?.email) return false;

    return true;
  };

  return (
    <RegisterContext.Provider
      value={{
        registerAddress,
        registerOwner,
        registerCarwash,
        registerSignup,
        registerHeroSignUp,
        setSurveyData,
        getRegisterData,
        canProceed,
      }}
    >
      {children}
    </RegisterContext.Provider>
  );
};

export const useRegister = () => {
  const context = useContext(RegisterContext);
  if (!context) {
    throw new Error("useRegister must be used within an RegisterProvider");
  }
  return context;
};
